/*
 * Batch tools (W2-11): generator side sheets, Paste special and Transform dialogs.
 * Design tokens only (src/ui/styles/tokens.css). State is never shown by colour alone: errors and pauses carry an
 * icon and words. Sheets are 360 px wide (src/ui/Sheet.module.css); every row stacks its label above the control.
 */

.form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.subtitle {
  margin: 0 0 var(--space-4);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

/* Fields side by side; their labels sit on top, so the tops line up (a field's helper line hangs below it). */
.row {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
}

.row > * {
  flex: 1 1 0;
  min-width: 0;
}

/* A field with a small button after it (seed + new seed): the field takes the room, the button sits on its line. */
.rowTight {
  display: flex;
  align-items: flex-end;
  gap: var(--space-2);
  min-width: 0;
}

.rowTight > :first-child {
  flex: 1 1 auto;
  min-width: 0;
}

.rowTight > :not(:first-child) {
  flex: 0 0 auto;
  margin-bottom: var(--space-1);
}

/* Checkbox and its label on one line; a long label wraps beside the box, never under it (UX-046). */
.check {
  flex-wrap: nowrap;
  align-items: flex-start;
}

.check > label {
  flex: 1 1 auto;
  min-width: 0;
}

.check > button {
  margin-top: 2px;
}

.group {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-3);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-1);
}

.groupTitle {
  margin: 0;
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--color-text);
}

.hint {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
}

.expression {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
}

.code {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-family: var(--font-mono);
  font-size: var(--text-sm);
  color: var(--color-text);
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.preview {
  margin: 0;
  padding: 0;
  list-style: none;
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.preview li {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  min-height: var(--control-h-sm);
  margin: 0;
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.statusError {
  color: var(--color-danger);
}

.statusWarn {
  color: var(--color-warning);
}

.footer {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
}

.footerStatus {
  flex: 1;
  min-width: 0;
}

.curve {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.curve > :first-child {
  flex: 0 0 auto;
}

.curve > :nth-child(2) {
  flex: 1 1 auto;
  min-width: 0;
}

.lineList {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  max-height: 180px;
  overflow: auto;
  padding: var(--space-2);
  border: var(--border-width) solid var(--color-border-input);
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
}

.ramp {
  display: grid;
  grid-template-columns: minmax(64px, auto) minmax(0, 1fr) minmax(0, 1fr);
  align-items: end;
  gap: var(--space-2);
}

.rampLabel {
  align-self: end;
  padding-bottom: var(--space-2);
  font-size: var(--text-sm);
  color: var(--color-text);
}

.error {
  margin: 0;
  color: var(--color-danger);
  font-size: var(--text-sm);
}

.dialogBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.summary {
  margin: 0;
  padding: var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
  font-size: var(--text-sm);
  color: var(--color-text);
}

.fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-3);
}
